import type {
  AppointmentWireItem,
  AppointmentWireStatus,
  SchedulingBlockWire,
} from '../../lib/api-client';

/**
 * Adapter puro da Tela 7 (Agenda) — derivação de slot, KPIs, ranges de vista e
 * mapas de rótulo. Sem React, 100% testável. Recebe `nowMs`/`cursorMs` como
 * parâmetro (sem `Date.now()` impuro na lógica — D7).
 *
 * Decisões (spec 2026-06-06): vistas/KPIs client-side (D3/D7); status real de 7
 * valores (D1); rótulos degradam honesto p/ valor desconhecido.
 */

export type CalendarView = 'month' | 'week' | 'day';

function pad2(n: number): string {
  return n.toString().padStart(2, '0');
}

/** Chave de dia em hora LOCAL (YYYY-MM-DD). */
export function dateKeyOf(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Domingo 00:00 da semana de `ms` (local). */
function startOfWeek(ms: number): number {
  const d = new Date(startOfDay(ms));
  d.setDate(d.getDate() - d.getDay());
  return d.getTime();
}

function addDaysMs(ms: number, n: number): number {
  const d = new Date(ms);
  d.setDate(d.getDate() + n);
  return d.getTime();
}

export interface AppointmentSlot {
  start: string; // HH:MM (local)
  end: string; // HH:MM (local)
  startHour: number;
  dateKey: string; // YYYY-MM-DD (local)
}

/** Deriva horário/dia do appointment a partir de `scheduledAt` + `durationMinutes`. */
export function deriveSlot(appt: AppointmentWireItem): AppointmentSlot {
  const startMs = new Date(appt.scheduledAt).getTime();
  const endMs = startMs + appt.durationMinutes * 60 * 1000;
  const s = new Date(startMs);
  const e = new Date(endMs);
  return {
    start: `${pad2(s.getHours())}:${pad2(s.getMinutes())}`,
    end: `${pad2(e.getHours())}:${pad2(e.getMinutes())}`,
    startHour: s.getHours(),
    dateKey: dateKeyOf(startMs),
  };
}

/**
 * O bloqueio (AvailabilityBlock) que cobre a célula (recurso, hora) do dia local do cursor,
 * se houver — "bloqueios inline na grade" recurso×tempo. Sobreposição half-open [início, fim):
 * um bloqueio que apenas encosta na borda da hora não a cobre. Puro/testável.
 */
export function blockForCell(
  blocks: SchedulingBlockWire[],
  resourceId: string,
  cursorMs: number,
  hour: number,
): SchedulingBlockWire | null {
  const cellStart = startOfDay(cursorMs) + hour * 60 * 60 * 1000;
  const cellEnd = cellStart + 60 * 60 * 1000;
  for (const b of blocks) {
    if (b.resourceId !== resourceId) continue;
    const bs = new Date(b.startsAt).getTime();
    const be = new Date(b.endsAt).getTime();
    if (bs < cellEnd && be > cellStart) return b;
  }
  return null;
}

export interface CalendarKpiData {
  today: number;
  week: number;
  awaitingConfirmation: number;
  noShow: number;
  upcomingConfirmed: number;
}

/** KPIs honestos derivados do range carregado (D3). */
export function deriveKpis(appts: AppointmentWireItem[], nowMs: number): CalendarKpiData {
  const todayKey = dateKeyOf(nowMs);
  const weekStart = startOfWeek(nowMs);
  const weekEnd = addDaysMs(weekStart, 7);
  let today = 0;
  let week = 0;
  let awaitingConfirmation = 0;
  let noShow = 0;
  let upcomingConfirmed = 0;
  for (const a of appts) {
    const at = new Date(a.scheduledAt).getTime();
    if (dateKeyOf(at) === todayKey) today += 1;
    if (at >= weekStart && at < weekEnd) week += 1;
    if (a.status === 'scheduled') awaitingConfirmation += 1;
    if (a.status === 'no_show') noShow += 1;
    if (a.status === 'confirmed' && at >= nowMs) upcomingConfirmed += 1;
  }
  return { today, week, awaitingConfirmation, noShow, upcomingConfirmed };
}

export interface CalendarRange {
  fromISO: string;
  toISO: string;
}

/** Range server-side (`from`/`to`) por vista — cobre a grade renderizada. */
export function rangeFor(view: CalendarView, cursorMs: number): CalendarRange {
  let fromMs: number;
  let toMs: number;
  if (view === 'day') {
    fromMs = startOfDay(cursorMs);
    toMs = addDaysMs(fromMs, 1);
  } else if (view === 'week') {
    fromMs = startOfWeek(cursorMs);
    toMs = addDaysMs(fromMs, 7);
  } else {
    // month: grade 6×7 a partir do domingo da semana do 1º dia do mês
    const d = new Date(startOfDay(cursorMs));
    d.setDate(1);
    fromMs = startOfWeek(d.getTime());
    toMs = addDaysMs(fromMs, 42);
  }
  return { fromISO: new Date(fromMs).toISOString(), toISO: new Date(toMs).toISOString() };
}

/** Novo cursor ao navegar ‹ › por vista. */
export function navCursor(view: CalendarView, cursorMs: number, dir: number): number {
  if (view === 'day') return addDaysMs(cursorMs, dir);
  if (view === 'week') return addDaysMs(cursorMs, dir * 7);
  const d = new Date(cursorMs);
  d.setMonth(d.getMonth() + dir);
  return d.getTime();
}

/** 7 dias (ms) da semana do cursor, começando no domingo. */
export function weekDays(cursorMs: number): number[] {
  const start = startOfWeek(cursorMs);
  return Array.from({ length: 7 }, (_, i) => addDaysMs(start, i));
}

/** 42 células (ms) da grade do mês do cursor. */
export function monthCells(cursorMs: number): number[] {
  const d = new Date(startOfDay(cursorMs));
  d.setDate(1);
  const start = startOfWeek(d.getTime());
  return Array.from({ length: 42 }, (_, i) => addDaysMs(start, i));
}

/**
 * Statuses TERMINAIS DE CADEIA: o appointment virou elo antigo — outro (`rescheduledFromId`
 * apontando p/ ele) carrega o compromisso. Não é um compromisso do dia; é histórico.
 * `cancelled`/`no_show`/`completed` NÃO entram aqui: são fatos do dia sem sucessor, e a
 * grade os mostra (com tom próprio) porque a operação precisa vê-los onde aconteceram.
 */
const CHAIN_TERMINAL_STATUSES: ReadonlySet<AppointmentWireStatus> = new Set<AppointmentWireStatus>([
  'rescheduled',
]);

/**
 * N2-09 (noturna 05/Set/2026): após reagendar 09:00→10:00, a grade mostrava DOIS blocos
 * vivos idênticos — o elo `rescheduled` renderizava igual ao sucessor e competia por slot.
 * Elo terminal de cadeia não vira bloco na grade (semana/dia/mês/recurso). `deriveKpis`
 * NÃO usa este filtro — os KPIs seguem contando o range carregado como antes.
 */
export function isGridLive(appt: Pick<AppointmentWireItem, 'status'>): boolean {
  return !CHAIN_TERMINAL_STATUSES.has(appt.status);
}

/** Appointments que a grade renderiza como bloco (vide `isGridLive`). */
export function gridAppts(appts: AppointmentWireItem[]): AppointmentWireItem[] {
  return appts.filter(isGridLive);
}

/** Appointments de um dia (por dateKey local). */
export function apptsForDay(appts: AppointmentWireItem[], dateKey: string): AppointmentWireItem[] {
  return appts.filter((a) => deriveSlot(a).dateKey === dateKey);
}

const WEEK_HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18] as const;
export { WEEK_HOURS };

// ─── Frente B Sessão 1 — vista recurso-cêntrica (colunas por recurso) ───

export interface ResourceDayColumn {
  resourceId: string;
  appts: AppointmentWireItem[];
}

/**
 * Agrupa os appointments de UM dia (dateKey local) por recurso ocupado
 * (`appointmentResources`). Determinístico:
 *   - terminal liberado (cancelled/no_show/rescheduled) tem `appointmentResources` vazio
 *     (a cascata de lifecycle deleta o ledger) → cai em `unassigned`, não polui colunas;
 *   - serviço composto (>1 recurso) aparece em cada coluna que ocupa;
 *   - avulso/legado (sem recurso) cai em `unassigned`.
 * Cada lista é ordenada por horário (asc). Não recebe `Date.now()` (puro, testável).
 */
export function apptsByResourceForDay(
  appts: AppointmentWireItem[],
  dateKey: string,
): { byResource: Map<string, AppointmentWireItem[]>; unassigned: AppointmentWireItem[] } {
  const dayAppts = apptsForDay(appts, dateKey)
    .slice()
    .sort((a, b) => {
      return new Date(a.scheduledAt).getTime() - new Date(b.scheduledAt).getTime();
    });
  const byResource = new Map<string, AppointmentWireItem[]>();
  const unassigned: AppointmentWireItem[] = [];
  for (const a of dayAppts) {
    const occ = a.appointmentResources ?? [];
    if (occ.length === 0) {
      unassigned.push(a);
      continue;
    }
    for (const r of occ) {
      const list = byResource.get(r.resourceId) ?? [];
      list.push(a);
      byResource.set(r.resourceId, list);
    }
  }
  return { byResource, unassigned };
}

/**
 * Ocupação de um recurso de GRUPO num dado horário de início (HH:MM local): quantos
 * appointments ATIVOS ocupam o recurso naquele slot. Conta posse real (`appointmentResources`),
 * não vagas. Usado para o medidor "N/capacidade" do slot de grupo.
 */
export function occupancyAt(appts: AppointmentWireItem[], startHHMM: string): number {
  return appts.filter((a) => deriveSlot(a).start === startHHMM).length;
}

/**
 * Horas da grade Semana/Dia: 8-18h base + horas de agendamentos REAIS dos dias
 * exibidos fora desse intervalo — agendamento noturno/matinal não pode sumir
 * da grade (o KPI conta, a grade tem que mostrar).
 */
export function gridHours(appts: AppointmentWireItem[], dayKeys: string[]): number[] {
  const keys = new Set(dayKeys);
  const hours = new Set<number>(WEEK_HOURS);
  for (const a of appts) {
    const slot = deriveSlot(a);
    if (keys.has(slot.dateKey)) hours.add(slot.startHour);
  }
  return [...hours].sort((x, y) => x - y);
}

/** "YYYY-MM-DD" → "DD/MM/AAAA"; fora do formato devolve a entrada (honesto). */
export function formatDateKeyBR(dateKey: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateKey);
  const [, y, mo, d] = m ?? [];
  if (y === undefined || mo === undefined || d === undefined) return dateKey;
  return `${d}/${mo}/${y}`;
}

// ─── Mapas de rótulo — conhecidos + degradação honesta (D1) ───

interface StatusMeta {
  label: string;
  /** Sufixo do modificador CSS (`.cal-week__appt--<tone>` / `.appt-card--<tone>`). */
  tone: string;
}

const STATUS_META: Record<AppointmentWireStatus, StatusMeta> = {
  scheduled: { label: 'Agendado', tone: 'scheduled' },
  confirmed: { label: 'Confirmado', tone: 'confirmed' },
  imminent: { label: 'Iminente', tone: 'imminent' },
  completed: { label: 'Concluído', tone: 'completed' },
  cancelled: { label: 'Cancelado', tone: 'cancelled' },
  no_show: { label: 'Não compareceu', tone: 'no-show' },
  rescheduled: { label: 'Reagendado', tone: 'rescheduled' },
};

export function statusMeta(status: AppointmentWireStatus): StatusMeta {
  return STATUS_META[status] ?? { label: String(status), tone: 'scheduled' };
}

const TYPE_LABELS: Record<string, string> = {
  technical_visit: 'Visita técnica',
  in_person_meeting: 'Reunião presencial',
  video_call: 'Videochamada',
  phone_call: 'Ligação',
  delivery: 'Entrega',
  other: 'Outro',
};

export function typeLabel(type: string): string {
  return TYPE_LABELS[type] ?? type;
}

/**
 * Linha de identificação "Pessoa · Bond" do card/drawer (Brief 2B Tela 7 §4).
 * Une `personName` + `bondLabel` (já resolvidos no backend) com " · ", omitindo
 * partes nulas/vazias. Degrada honesto: appointment avulso → string vazia (o
 * card cai para o tipo, sem "—" fabricado).
 */
export function personBondLine(
  appt: Pick<AppointmentWireItem, 'personName' | 'bondLabel'>,
): string {
  return [appt.personName, appt.bondLabel]
    .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
    .join(' · ');
}

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];
const DAY_NAMES_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
export { MONTH_NAMES, DAY_NAMES_SHORT };

/** Nome do mês (0-11) — sempre válido; `?? ''` satisfaz noUncheckedIndexedAccess. */
export function monthName(i: number): string {
  return MONTH_NAMES[i] ?? '';
}
/** Nome curto do dia da semana (0-6). */
export function dayNameShort(i: number): string {
  return DAY_NAMES_SHORT[i] ?? '';
}

/** Rótulo do range exibido na toolbar. */
export function rangeLabel(view: CalendarView, cursorMs: number): string {
  const d = new Date(cursorMs);
  if (view === 'month') return `${monthName(d.getMonth())} ${d.getFullYear()}`;
  if (view === 'day') {
    return `${dayNameShort(d.getDay())} · ${d.getDate()} de ${monthName(d.getMonth())} ${d.getFullYear()}`;
  }
  const start = new Date(startOfWeek(cursorMs));
  const end = new Date(addDaysMs(start.getTime(), 6));
  return `${start.getDate()}–${end.getDate()} de ${monthName(end.getMonth())} ${end.getFullYear()}`;
}
